import { formatSwedishTime, parseUtcDate } from "../../helpers/DateTime";

const emptySeries = () => ({ soc: [], power: [], powerOffered: [] });

export function getSampledValueTimestamp(item) {
    return item?.meterValueTimestamp || item?.createdDateTimeUtc || item?.timestamp;
}

function canonicalMeasurand(measurand) {
    return (measurand ?? "").replace(/\./g, "").toLowerCase();
}

function seriesPointKey(measurand, context, timestampMs) {
    return `${canonicalMeasurand(measurand)}|${context ?? ""}|${timestampMs}`;
}

function toKw(value, unit) {
    return unit === "w" ? value / 1000 : value;
}

function toSeriesPoint(item, value) {
    const time = getSampledValueTimestamp(item);
    if (!time) {
        return null;
    }

    const timestampMs = parseUtcDate(time).getTime();
    if (Number.isNaN(timestampMs)) {
        return null;
    }

    const measurand = item.measurand ?? "";
    const context = item.context ?? "";

    return {
        x: timestampMs,
        y: value,
        label: formatSwedishTime(time),
        key: seriesPointKey(measurand, context, timestampMs),
    };
}

export function dedupeAndSortSeriesPoints(points) {
    const seen = new Set();
    const unique = [];

    for (const point of points) {
        if (!point) {
            continue;
        }

        const key = point.key ?? String(point.x);
        if (seen.has(key)) {
            continue;
        }

        seen.add(key);
        unique.push(point);
    }

    unique.sort((a, b) => a.x - b.x);
    return unique;
}

function isZeroPoint(point) {
    return Number(point?.y) === 0;
}

function firstReportedTimestamp(series) {
    let startX = null;

    for (const point of [...(series?.soc ?? []), ...(series?.power ?? [])]) {
        if (isZeroPoint(point)) {
            continue;
        }

        if (startX == null || point.x < startX) {
            startX = point.x;
        }
    }

    return startX;
}

function pointsFrom(points, startX) {
    if (startX == null) {
        return [];
    }

    return points.filter((point) => point.x >= startX);
}

function finalizeSeries(series) {
    const startX = firstReportedTimestamp(series);

    return {
        soc: pointsFrom(series.soc, startX),
        power: pointsFrom(series.power, startX),
        powerOffered: pointsFrom(series.powerOffered, startX),
    };
}

function mapSampledValuesToSeriesRaw(list) {
    const series = emptySeries();
    if (!Array.isArray(list)) {
        return series;
    }

    for (const item of list) {
        const value = Number(item?.measuredValue ?? item?.value);
        if (Number.isNaN(value)) {
            continue;
        }

        const point = toSeriesPoint(item, value);
        if (!point) {
            continue;
        }

        const measurand = canonicalMeasurand(item.measurand);
        const unit = (item.unit ?? "").toLowerCase();

        if (unit === "%" || measurand === "soc") {
            series.soc.push(point);
        }

        if (measurand === "poweractiveimport") {
            series.power.push({ ...point, y: toKw(value, unit) });
        }

        if (measurand === "poweroffered") {
            series.powerOffered.push({ ...point, y: toKw(value, unit) });
        }
    }

    return {
        soc: dedupeAndSortSeriesPoints(series.soc),
        power: dedupeAndSortSeriesPoints(series.power),
        powerOffered: dedupeAndSortSeriesPoints(series.powerOffered),
    };
}

export function mapSampledValuesToSeries(list) {
    return finalizeSeries(mapSampledValuesToSeriesRaw(list));
}

export function mergeSampledValuesIntoSeries(prev, list, maxPoints = 200) {
    const incoming = mapSampledValuesToSeriesRaw(list);
    const merge = (existing, next) => {
        const merged = dedupeAndSortSeriesPoints([...(existing ?? []), ...next]);
        return maxPoints && merged.length > maxPoints ? merged.slice(-maxPoints) : merged;
    };

    return finalizeSeries({
        soc: merge(prev?.soc, incoming.soc),
        power: merge(prev?.power, incoming.power),
        powerOffered: merge(prev?.powerOffered, incoming.powerOffered),
    });
}
